import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { tosThumbnail } from "../../../utils/tosImage";
import {
  readImageFileDimensions,
  validateImageDimensions,
  validateImageAspectRatio,
  aspectRatioToastParams,
} from "../../../utils/imageDimensions";
import {
  uploadAssetBaseRefImage,
  uploadAnalysisBaseImage,
  deleteAssetBaseRefImage,
  generateBaseImage,
  isApiError,
  selectBaseImage,
} from "../../../api";
import { useToast } from "../../../components/Toast";
import type { ElementType } from "../types";
import type { ImageModel } from "../../../types";
import { MAX_TOTAL_REF_IMAGES } from "../utils/editDefaults";
import { generateRequestId } from "../utils/requestId";
import { useImageUploadZone } from "../../../hooks/useImageUploadZone";
import { useImageAspectRatioGuard } from "../../../hooks/useImageAspectRatioGuard";
import { getDropHighlightStyle } from "../../../utils/imageFiles";
import { UploadZoneHint } from "../../../components/UploadZoneHint";
import { Lightbox } from "./Lightbox";
import type {
  ActiveImageTask,
  SlotFailure,
} from "../utils/activeImageTasks";
import type { BaseMetadataField } from "../utils/baseMetadata";

const MAX_ACTIVE_BASE_TASKS_PER_ASSET = 5; // 与后端 MaxActiveBaseImageTasksPerAsset 一致

export const BaseImageBlock: React.FC<{
  projectId: string;
  elementType: ElementType;
  name: string;
  fixedPrompt: string;
  mostEpisodesPrompt: string;
  initBaseRefImages: string[];
  initBaseImageUrls: string[];
  initSelectedBaseImageUrl?: string;
  conflictFields?: BaseMetadataField[];
  analysisAuthorityRevision: number;
  onSelectBaseImage?: (url: string) => void;
  /** Reload authoritative analysis metadata after any successful base-image mutation. */
  onBaseGenerated?: () => void | boolean | Promise<void | boolean>;
  activeBaseTasks?: ActiveImageTask[];
  failedBaseTasks?: SlotFailure[];
  projectRefImages?: string[];
  onSearchSelect?: (currentRefCount: number) => void;
  searchImageCount?: number;
  searchTaskProgress?: { completed: number; total: number };
  onGenerateKeywords?: () => void;
  keywordStatus?:
    | "none"
    | "generating"
    | "pending"
    | "searching"
    | "completed"
    | "failed";
  imageModels?: ImageModel[];
  defaultImagePlatform?: string;
  defaultImageModel?: string;
}> = ({
  projectId,
  elementType,
  name,
  fixedPrompt,
  mostEpisodesPrompt,
  initBaseRefImages,
  initBaseImageUrls,
  initSelectedBaseImageUrl,
  conflictFields = [],
  analysisAuthorityRevision,
  onSelectBaseImage,
  onBaseGenerated,
  projectRefImages: initProjectRefImages = [],
  onSearchSelect,
  searchImageCount,
  searchTaskProgress,
  onGenerateKeywords,
  keywordStatus,
  activeBaseTasks = [],
  failedBaseTasks = [],
  imageModels = [],
  defaultImagePlatform,
  defaultImageModel,
}) => {
  const { t } = useTranslation("projectDetail");
  const toast = useToast();
  const guardAspectRatio = useImageAspectRatioGuard();
  const projRefExcludeKey = `base-proj-ref-excluded:${projectId}:${elementType}:${name}`;
  const loadExcluded = () => {
    try {
      return JSON.parse(
        localStorage.getItem(projRefExcludeKey) || "[]",
      ) as string[];
    } catch {
      return [];
    }
  };
  const [baseRefImages, setBaseRefImages] =
    useState<string[]>(initBaseRefImages);
  const [excludedProjectRefUrls, setExcludedProjectRefUrls] =
    useState<string[]>(loadExcluded);
  const [baseImageUrls, setBaseImageUrls] =
    useState<string[]>(initBaseImageUrls);
  const remoteSelectedBaseImageUrl = conflictFields.includes("selectedBaseImageUrl")
    ? undefined
    : initSelectedBaseImageUrl;
  const [selectedBaseImageUrl, setSelectedBaseImageUrl] = useState<
    string | undefined
  >(remoteSelectedBaseImageUrl);
  const [serverConflictFields, setServerConflictFields] = useState<BaseMetadataField[]>([]);
  const effectiveConflictFields = Array.from(
    new Set<BaseMetadataField>([...conflictFields, ...serverConflictFields]),
  );
  const hasRefConflict = effectiveConflictFields.includes("baseRefImages");
  const hasCandidateConflict = effectiveConflictFields.includes("baseImageUrls");
  const hasArrayConflict = hasRefConflict || hasCandidateConflict;
  const [submitting, setSubmitting] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const selectingRef = useRef(false);
  const inFlightBaseCount = (activeBaseTasks ?? []).length;
  const isBaseInFlight = inFlightBaseCount > 0 || submitting;
  const atBaseLimit = inFlightBaseCount >= MAX_ACTIVE_BASE_TASKS_PER_ASSET;
  const [genError, setGenError] = useState<string | null>(null);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const basePromptKey = `base-prompt:${projectId}:${elementType}:${name}`;
  const defaultPrompt = `${fixedPrompt}\n${mostEpisodesPrompt}`;
  const [promptValue, setPromptValue] = useState(
    () => localStorage.getItem(basePromptKey) || defaultPrompt,
  );
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [uploadingBaseImage, setUploadingBaseImage] = useState(false);
  const [hoveredBaseIndex, setHoveredBaseIndex] = useState<number | null>(null);
  const [selectedModelId, setSelectedModelId] = useState(() => {
    const matched = imageModels.find(
      (m) =>
        m.platform === defaultImagePlatform && m.model === defaultImageModel,
    );
    return (matched || imageModels[0])?.id || "";
  });

  useEffect(() => {
    if (!selectedModelId && imageModels.length > 0) {
      const matched = imageModels.find(
        (m) =>
          m.platform === defaultImagePlatform && m.model === defaultImageModel,
      );
      setSelectedModelId((matched || imageModels[0]).id);
    }
  }, [imageModels, defaultImagePlatform, defaultImageModel]); // eslint-disable-line react-hooks/exhaustive-deps

  const baseRefImagesSignature = JSON.stringify(initBaseRefImages);
  const baseImageUrlsSignature = JSON.stringify(initBaseImageUrls);

  useEffect(() => {
    setBaseRefImages([...initBaseRefImages]);
  }, [baseRefImagesSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPreviewIndex(currentIndex => {
      if (currentIndex === null) return null;
      const currentUrl = baseImageUrls[currentIndex];
      if (!currentUrl) return null;
      const nextIndex = initBaseImageUrls.indexOf(currentUrl);
      return nextIndex >= 0 ? nextIndex : null;
    });
    setBaseImageUrls([...initBaseImageUrls]);
  }, [baseImageUrlsSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedBaseImageUrl(remoteSelectedBaseImageUrl);
  }, [remoteSelectedBaseImageUrl]);

  useEffect(() => {
    setServerConflictFields([]);
  }, [analysisAuthorityRevision]);

  const captureBaseMetadataConflict = (error: unknown): boolean => {
    if (!isApiError(error) || error.status !== 409 || error.code !== "base_metadata_conflict") {
      return false;
    }
    const fields = (error.fields ?? []).filter(
      (field): field is BaseMetadataField =>
        field === "baseRefImages" ||
        field === "baseImageUrls" ||
        field === "selectedBaseImageUrl",
    );
    if (fields.length === 0) return false;
    if (fields.includes("selectedBaseImageUrl")) {
      setSelectedBaseImageUrl(undefined);
    }
    setServerConflictFields((current) =>
      Array.from(new Set<BaseMetadataField>([...current, ...fields])),
    );
    return true;
  };

  const handleUploadRef = async (file: File) => {
    if (!(await guardAspectRatio(file))) return;
    try {
      const res = await uploadAssetBaseRefImage(
        projectId,
        elementType,
        name,
        file,
      );
      setBaseRefImages(res.baseRefImages);
      onBaseGenerated?.();
    } catch (error: unknown) {
      if (!captureBaseMetadataConflict(error)) {
        toast.error(t("baseImage.uploadFailed"));
      }
    }
  };

  const handleUploadBaseImage = async (file: File) => {
    setUploadingBaseImage(true);
    try {
      let dimensions;
      try {
        dimensions = await readImageFileDimensions(file);
      } catch {
        toast.error(t("characterStyle.uploadImageReadFailed"));
        return;
      }
      const dimensionError = validateImageDimensions(dimensions);
      if (dimensionError) {
        toast.error(
          t("characterStyle.uploadImageDimensionInvalid", {
            ...dimensionError,
          }),
        );
        return;
      }
      const aspectError = validateImageAspectRatio(dimensions);
      if (aspectError) {
        toast.error(t("imageAspectRatioInvalid", aspectRatioToastParams(aspectError)));
        return;
      }
      const res = await uploadAnalysisBaseImage(
        projectId,
        elementType,
        name,
        file,
      );
      setBaseImageUrls((prev) =>
        prev.includes(res.imageUrl) ? prev : [...prev, res.imageUrl],
      );
      onBaseGenerated?.();
    } catch (error: unknown) {
      if (!captureBaseMetadataConflict(error)) {
        toast.error(t("elementCard.uploadImageFailed", { error: t("baseImage.uploadFailed") }));
      }
    } finally {
      setUploadingBaseImage(false);
    }
  };

  const handleDeleteRef = async (url: string) => {
    try {
      const res = await deleteAssetBaseRefImage(
        projectId,
        elementType,
        name,
        url,
      );
      setBaseRefImages(res.baseRefImages);
      onBaseGenerated?.();
    } catch (error: unknown) {
      if (!captureBaseMetadataConflict(error)) {
        toast.error(t("baseImage.mutationFailed"));
      }
    }
  };

  const handleGenerate = async () => {
    setGenError(null);
    setSubmitting(true);
    try {
      const selectedModel = imageModels.find((m) => m.id === selectedModelId);
      const requestId = generateRequestId();
      await generateBaseImage(
        projectId,
        elementType,
        name,
        promptValue,
        requestId,
        excludedProjectRefUrls.length ? excludedProjectRefUrls : undefined,
        selectedModel?.platform,
        selectedModel?.model,
      );
      onBaseGenerated?.(); // 触发父级 loadAnalysis → 刷新 active-tasks，新任务以占位出现
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 409 && error.code === "too_many_active_tasks") {
        toast.warning(t("elementCard.tooManyActiveTasks"));
      } else if (captureBaseMetadataConflict(error)) {
        // The field-scoped explanation is rendered below from structured fields.
      } else {
        setGenError(t("baseImage.generateFailed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const persistBasePrompt = () => {
    if (promptValue !== defaultPrompt) localStorage.setItem(basePromptKey, promptValue);
    else localStorage.removeItem(basePromptKey);
  };

  const handleSelect = async (url: string) => {
    if (hasArrayConflict || selectingRef.current) return;
    selectingRef.current = true;
    setSelecting(true);
    try {
      await selectBaseImage(projectId, elementType, name, url);
      setSelectedBaseImageUrl(url);
      setServerConflictFields((fields) =>
        fields.filter((field) => field !== "selectedBaseImageUrl"),
      );
      onSelectBaseImage?.(url);
      onBaseGenerated?.();
    } catch (error: unknown) {
      if (!captureBaseMetadataConflict(error)) {
        toast.error(t("baseImage.mutationFailed"));
      }
    } finally {
      selectingRef.current = false;
      setSelecting(false);
    }
  };

  const maxRefImages = Math.max(0, MAX_TOTAL_REF_IMAGES - initProjectRefImages.length);
  const canGenerate = true;

  const refZone = useImageUploadZone({
    multiple: true,
    maxFiles: Math.max(0, maxRefImages - baseRefImages.length),
    disabled: hasRefConflict,
    onFiles: async (imgs) => {
      for (const f of imgs) await handleUploadRef(f);
    },
  });
  const baseZone = useImageUploadZone({
    onFiles: (imgs) => handleUploadBaseImage(imgs[0]),
    disabled: uploadingBaseImage || hasCandidateConflict,
  });

  const modelSelect = imageModels.length > 0 && (
    <select
      className="input"
      value={selectedModelId}
      onChange={(e) => setSelectedModelId(e.target.value)}
      title={t("baseImage.selectModelTitle")}
      style={{
        fontSize: "0.75rem",
        padding: "2px 6px",
        height: "26px",
        borderColor: "var(--theme-scene)",
        borderRadius: "4px",
        maxWidth: "160px",
      }}
    >
      {imageModels.map((m) => (
        <option key={m.id} value={m.id}>
          {m.displayName || m.model}
        </option>
      ))}
    </select>
  );

  const shownError = genError;

  return (
    <div
      style={{
        background: "var(--surface-2)",
        borderRadius: "6px",
        padding: "0.75rem",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        {effectiveConflictFields.map((field) => (
          <p
            key={field}
            data-testid={`base-metadata-conflict-${field}`}
            style={{ margin: 0, fontSize: "0.78rem", color: "var(--danger)" }}
          >
            {t(`baseImage.conflict.${field}`)}
          </p>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Link
            to={`/projects/${projectId}/style-test?elementType=${encodeURIComponent(elementType)}&elementName=${encodeURIComponent(name)}`}
            style={{
              fontSize: "0.75rem",
              color: "var(--theme-scene)",
              textDecoration: "none",
              border: "1px solid var(--theme-scene)",
              borderRadius: "4px",
              padding: "2px 8px",
              whiteSpace: "nowrap",
            }}
          >
            {t("baseImage.styleTest")}
          </Link>
          {isBaseInFlight && (
            <span style={{ fontSize: "0.7rem", color: "var(--theme-scene)" }}>
              {t("generating", { ns: "common" })}
            </span>
          )}
        </div>
        {/* 提示词区域 */}
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}
        >
          {editingPrompt ? (
            <div>
              <textarea
                value={promptValue}
                onChange={(e) => setPromptValue(e.target.value)}
                style={{
                  width: "100%",
                  minHeight: "80px",
                  fontSize: "0.82rem",
                  border: "1px solid var(--theme-scene)",
                  borderRadius: "4px",
                  padding: "0.5rem",
                  resize: "vertical",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  lineHeight: 1.5,
                }}
                placeholder={t("baseImage.baseImagePromptPlaceholder")}
              />
              <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.4rem", flexWrap: "wrap" }}>
                <button
                  className="btn"
                  style={{ background: "var(--theme-scene)", color: "#fff", borderColor: "var(--theme-scene)", padding: "4px 12px", fontSize: "0.8rem" }}
                  onClick={() => { persistBasePrompt(); setEditingPrompt(false); }}
                >
                  {t("save", { ns: "common" })}
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ padding: "4px 12px", fontSize: "0.8rem" }}
                  onClick={() => {
                    setEditingPrompt(false);
                    setPromptValue(localStorage.getItem(basePromptKey) || defaultPrompt);
                  }}
                >
                  {t("cancel", { ns: "common" })}
                </button>
                <button
                  className="btn"
                  style={{ background: "var(--theme-scene)", color: "#fff", borderColor: "var(--theme-scene)", padding: "4px 12px", fontSize: "0.8rem" }}
                  onClick={() => { persistBasePrompt(); setEditingPrompt(false); handleGenerate(); }}
                  disabled={submitting || atBaseLimit || hasArrayConflict}
                  title={
                    hasArrayConflict
                      ? t("baseImage.conflict.blockedAction")
                      : atBaseLimit
                        ? t("elementCard.tooManyActiveTasks")
                        : undefined
                  }
                >
                  {submitting ? t("generating", { ns: "common" }) : t("elementCard.saveAndGenerate")}
                </button>
              </div>
            </div>
          ) : (
            <div
              style={{
                background: "var(--primary-light)",
                borderRadius: "4px",
                padding: "0.5rem 0.75rem",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "0.75rem",
                  color: "var(--text-3)",
                }}
              >
                {t("baseImage.generatePrompt")}
              </p>
              {promptValue ? (
                <p
                  style={{
                    margin: "0.3rem 0 0.4rem 0",
                    fontSize: "0.82rem",
                    color: "var(--theme-scene)",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {promptValue}
                </p>
              ) : (
                <p
                  style={{
                    margin: "0.3rem 0 0.4rem 0",
                    fontSize: "0.82rem",
                    color: "var(--text-3)",
                  }}
                >
                  {t("baseImage.noPrompt")}
                </p>
              )}
              <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                {modelSelect}
                <button
                  style={{
                    background: "none",
                    border: "1px solid var(--theme-scene)",
                    color: "var(--theme-scene)",
                    borderRadius: "4px",
                    padding: "2px 8px",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                  }}
                  onClick={() => setEditingPrompt(true)}
                >
                  {t("edit", { ns: "common" })}
                </button>
                {promptValue && (
                  <button
                    style={{
                      background: "none",
                      border: "1px solid var(--theme-scene)",
                      color: "var(--theme-scene)",
                      borderRadius: "4px",
                      padding: "2px 8px",
                      fontSize: "0.75rem",
                      cursor: "pointer",
                    }}
                    onClick={() => navigator.clipboard.writeText(promptValue)}
                  >
                    {t("copy", { ns: "common" })}
                  </button>
                )}
                <button
                  style={{
                    background: "none",
                    border: "1px solid var(--text-3)",
                    color: "var(--text-3)",
                    borderRadius: "4px",
                    padding: "2px 8px",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setPromptValue(defaultPrompt);
                    setExcludedProjectRefUrls([]);
                    localStorage.removeItem(projRefExcludeKey);
                    localStorage.removeItem(basePromptKey);
                  }}
                  title={t("baseImage.resetTitle")}
                >
                  {t("reset", { ns: "common" })}
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={!canGenerate || submitting || atBaseLimit || hasArrayConflict}
                  title={
                    hasArrayConflict
                      ? t("baseImage.conflict.blockedAction")
                      : atBaseLimit
                        ? t("elementCard.tooManyActiveTasks")
                        : undefined
                  }
                  style={{
                    background:
                      canGenerate && !submitting
                        ? "var(--theme-scene)"
                        : "rgba(59,130,246,0.1)",
                    border: "none",
                    color:
                      canGenerate && !submitting
                        ? "white"
                        : "var(--theme-scene)",
                    borderRadius: "4px",
                    padding: "2px 10px",
                    fontSize: "0.75rem",
                    cursor:
                      canGenerate && !submitting ? "pointer" : "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {submitting && (
                    <span
                      className="loading-spinner"
                      style={{
                        width: "10px",
                        height: "10px",
                        borderWidth: "2px",
                      }}
                    />
                  )}
                  {submitting
                    ? t("generating", { ns: "common" })
                    : baseImageUrls.length > 0
                      ? t("elementCard.regenerate")
                      : t("baseImage.generateBaseImage")}
                </button>
              </div>
              {shownError && (
                <p
                  style={{
                    margin: "0.4rem 0 0 0",
                    fontSize: "0.78rem",
                    color: "var(--danger)",
                  }}
                >
                  &#10060; {shownError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* 参考图区域 */}
        <div style={{ marginTop: "0.5rem" }}>
          <p
            style={{
              margin: "0 0 0.3rem",
              fontSize: "0.75rem",
              color: "var(--text-3)",
            }}
          >
            {t("baseImage.refImages", {
              current: baseRefImages.length,
              total: maxRefImages,
            })}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.4rem",
              alignItems: "flex-end",
            }}
          >
            {baseRefImages.map((url, i) => (
              <div key={i} style={{ position: "relative", flexShrink: 0 }}>
                <img
                  src={tosThumbnail(url, 175)}
                  alt=""
                  style={{
                    width: "175px",
                    height: "175px",
                    objectFit: "contain",
                    borderRadius: "4px",
                    border: "1px solid var(--border)",
                    display: "block",
                  }}
                />
                <button
                  onClick={() => handleDeleteRef(url)}
                  disabled={hasRefConflict}
                  data-testid="baseimage-ref-remove"
                  style={{
                    position: "absolute",
                    top: "-5px",
                    right: "-5px",
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    background: "var(--danger)",
                    color: "white",
                    border: "none",
                    cursor: hasRefConflict ? "not-allowed" : "pointer",
                    fontSize: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            {baseRefImages.length < maxRefImages && (
              <label
                {...refZone.zoneProps}
                data-testid="baseimage-ref-dropzone"
                style={{
                  width: "175px",
                  height: "175px",
                  border: "1px dashed var(--border)",
                  borderRadius: "4px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: hasRefConflict ? "not-allowed" : "pointer",
                  color: "var(--text-3)",
                  gap: "2px",
                  padding: 0,
                  ...getDropHighlightStyle(refZone.isOver),
                }}
              >
                <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>+</span>
                <span style={{ fontSize: "0.8rem" }}>
                  {t("baseImage.upload")}
                </span>
                <UploadZoneHint />
                <input
                  type="file"
                  accept="image/*"
                  disabled={hasRefConflict}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUploadRef(f);
                    e.target.value = "";
                  }}
                />
              </label>
            )}
            {baseRefImages.length < maxRefImages &&
              (() => {
                const hasKeywords =
                  searchTaskProgress && searchTaskProgress.total > 0;
                if (hasKeywords && onSearchSelect) {
                  return (
                    <button
                      onClick={() => onSearchSelect(baseRefImages.length)}
                      disabled={hasRefConflict}
                      style={{
                        width: "175px",
                        height: "175px",
                        flexShrink: 0,
                        background: "none",
                        border: "1px dashed var(--theme-scene)",
                        color: "var(--theme-scene)",
                        borderRadius: "4px",
                        cursor: hasRefConflict ? "not-allowed" : "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "2px",
                        padding: 0,
                      }}
                      title={t("elementCard.searchRefTitle")}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--theme-scene)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          lineHeight: 1,
                          textAlign: "center",
                        }}
                      >
                        {searchTaskProgress!.completed <
                        searchTaskProgress!.total
                          ? t("elementCard.searchProgress", {
                              completed: searchTaskProgress!.completed,
                              total: searchTaskProgress!.total,
                            })
                          : t("elementCard.searchCount", {
                              count: searchImageCount,
                            })}
                      </span>
                    </button>
                  );
                }
                if (onGenerateKeywords && keywordStatus !== "completed") {
                  const isGenerating = keywordStatus === "generating";
                  return (
                    <button
                      onClick={() => {
                        if (!isGenerating) onGenerateKeywords();
                      }}
                      disabled={isGenerating}
                      style={{
                        width: "175px",
                        height: "175px",
                        flexShrink: 0,
                        background: "none",
                        border: "1px dashed var(--theme-scene)",
                        color: "var(--theme-scene)",
                        borderRadius: "4px",
                        cursor: isGenerating ? "wait" : "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                        padding: "0.5rem",
                        opacity: isGenerating ? 0.6 : 1,
                      }}
                      title={t("search.generateKeywordsTitle")}
                    >
                      {isGenerating ? (
                        <>
                          <span
                            className="loading-spinner"
                            style={{
                              width: "16px",
                              height: "16px",
                              borderWidth: "2px",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "0.75rem",
                              lineHeight: 1.2,
                              textAlign: "center",
                            }}
                          >
                            {t("search.generatingKeywords")}
                          </span>
                        </>
                      ) : (
                        <>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--theme-scene)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                          </svg>
                          <span
                            style={{
                              fontSize: "0.75rem",
                              lineHeight: 1.2,
                              textAlign: "center",
                            }}
                          >
                            {t("search.generateSearchKeywords")}
                          </span>
                        </>
                      )}
                    </button>
                  );
                }
                return null;
              })()}
          </div>
        </div>

        {/* 候选基础图（无候选时也显示，保留上传入口） */}
        <div style={{ marginTop: "0.5rem" }}>
            <p
              style={{
                margin: "0 0 0.3rem",
                fontSize: "0.75rem",
                color: "var(--text-3)",
              }}
            >
              {t("baseImage.candidateImages")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                flexWrap: "wrap",
                padding: "6px 6px 4px",
              }}
            >
              {(activeBaseTasks ?? []).map((task) => (
                <div
                  key={task.taskId}
                  data-testid="base-inflight-placeholder"
                  style={{
                    width: "250px", height: "250px", flexShrink: 0,
                    border: "1px dashed var(--border)", borderRadius: "6px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "var(--surface-2)",
                  }}
                >
                  <span className="loading-spinner" style={{ width: "24px", height: "24px", borderWidth: "3px" }} />
                </div>
              ))}
              {(failedBaseTasks ?? []).map((f) => (
                <div
                  key={f.taskId}
                  data-testid="base-failed-placeholder"
                  title={
                    f.failureStage === "persist"
                      ? t("baseImage.persistenceFailureStage")
                      : t("elementCard.generateFailed")
                  }
                  style={{
                    width: "250px", height: "250px", flexShrink: 0,
                    border: "1px dashed var(--danger)", borderRadius: "6px",
                    background: "rgba(239, 68, 68, 0.08)", color: "var(--danger)",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    gap: "4px", cursor: "help",
                  }}
                >
                  <span style={{ fontSize: "1.6rem", lineHeight: 1 }}>&#10060;</span>
                  <span style={{ fontSize: "0.72rem" }}>
                    {f.failureStage === "persist"
                      ? t("baseImage.persistenceFailureStage")
                      : t("elementCard.generateFailed")}
                  </span>
                </div>
              ))}
              {baseImageUrls.map((url, i) => {
                const isSelected = selectedBaseImageUrl === url;
                return (
                  <div
                    key={i}
                    data-testid="baseimage-candidate"
                    style={{
                      position: "relative",
                      flexShrink: 0,
                      cursor: hasArrayConflict || selecting ? "not-allowed" : "pointer",
                    }}
                    onMouseEnter={() => setHoveredBaseIndex(i)}
                    onMouseLeave={() => setHoveredBaseIndex(null)}
                    onClick={() => {
                      if (!hasArrayConflict && !selecting) handleSelect(url);
                    }}
                  >
                    <img
                      src={tosThumbnail(url, 250)}
                      alt=""
                      style={{
                        width: "250px",
                        height: "250px",
                        objectFit: "contain",
                        borderRadius: "6px",
                        display: "block",
                        outline: isSelected
                          ? "4px solid var(--theme-scene)"
                          : "none",
                        outlineOffset: "2px",
                      }}
                    />
                    {isSelected && (
                      <div
                        data-testid="baseimage-selected"
                        style={{
                          position: "absolute",
                          top: "4px",
                          right: "4px",
                          background: "var(--theme-scene)",
                          borderRadius: "50%",
                          width: "22px",
                          height: "22px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "13px",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                          color: "white",
                        }}
                      >
                        ✓
                      </div>
                    )}
                    {hoveredBaseIndex === i && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewIndex(i);
                        }}
                        style={{
                          position: "absolute",
                          bottom: "6px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          background: "rgba(0,0,0,0.65)",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          padding: "2px 8px",
                          fontSize: "0.68rem",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("elementCard.showLargeImage")}
                      </button>
                    )}
                  </div>
                );
              })}
              {/* 上传自定义基础图 */}
              <label
                {...baseZone.zoneProps}
                data-testid="baseimage-candidate-dropzone"
                style={{
                  width: "250px",
                  height: "250px",
                  flexShrink: 0,
                  border: "1px dashed var(--border)",
                  borderRadius: "6px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  cursor:
                    uploadingBaseImage || hasCandidateConflict
                      ? "not-allowed"
                      : "pointer",
                  background: "var(--surface-2)",
                  color: "var(--text-3)",
                  ...getDropHighlightStyle(baseZone.isOver),
                }}
              >
                {uploadingBaseImage ? (
                  <span
                    className="loading-spinner"
                    style={{
                      width: "20px",
                      height: "20px",
                      borderWidth: "2px",
                    }}
                  />
                ) : (
                  <span style={{ fontSize: "1.6rem", lineHeight: 1 }}>+</span>
                )}
                <span style={{ fontSize: "0.72rem" }}>
                  {uploadingBaseImage
                    ? t("uploading", { ns: "common" })
                    : t("elementCard.uploadImage")}
                </span>
                {!uploadingBaseImage && <UploadZoneHint />}
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploadingBaseImage || hasCandidateConflict}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUploadBaseImage(f);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
        </div>
      </div>

      {previewIndex !== null &&
        ReactDOM.createPortal(
          <Lightbox
            urls={baseImageUrls}
            index={previewIndex}
            onClose={() => setPreviewIndex(null)}
            onChange={setPreviewIndex}
            selectedImageUrl={selectedBaseImageUrl}
            onSelect={hasArrayConflict || selecting ? undefined : handleSelect}
          />,
          document.body,
        )}
    </div>
  );
};
